
import { useState, useRef, useEffect, memo } from 'react';
import { cn } from '@/lib/utils';
import { getOptimizedImageUrl, getPreloadImageUrl, getPropertyCardImageUrl } from '@/lib/cloudinary';

interface OptimizedCloudinaryImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  quality?: 'auto' | number;
  usePropertyCardOptimization?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

const OptimizedCloudinaryImage = memo(({ 
  src, 
  alt, 
  className = '', 
  width,
  height,
  priority = false,
  quality = 'auto',
  usePropertyCardOptimization = false,
  onLoad,
  onError 
}: OptimizedCloudinaryImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLDivElement>(null);

  // Get optimized URLs - use specialized function for property cards
  const optimizedSrc = usePropertyCardOptimization 
    ? getPropertyCardImageUrl(src)
    : getOptimizedImageUrl(src, { width, height, quality });
  const preloadSrc = getPreloadImageUrl(src);

  useEffect(() => {
    if (priority) return; // Skip intersection observer for priority images

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  return (
    <div ref={imgRef} className={cn('relative overflow-hidden', className)}>
      {/* Low quality placeholder */}
      {!isLoaded && isInView && (
        <img
          src={preloadSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 transition-opacity duration-300"
          aria-hidden="true"
        />
      )}

      {/* Main optimized image */}
      {isInView && !hasError && (
        <img
          src={optimizedSrc}
          alt={alt}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? "eager" : "lazy"}
          style={{ willChange: 'transform' }}
          {...(width && height && { width, height })}
        />
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
          <div className="text-gray-400 text-center">
            <svg className="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
            <span className="text-xs">Error al cargar</span>
          </div>
        </div>
      )}
    </div>
  );
});

OptimizedCloudinaryImage.displayName = 'OptimizedCloudinaryImage';

export default OptimizedCloudinaryImage;
